import { ComponentProps } from 'react';
import barclaysLogo from '../../../../../assets/images/logo-barclays.svg';
import hsbcLogo from '../../../../../assets/images/logo-hsbc.svg';
import seapointLogo from '../../../../../assets/images/logo-seapoint.svg';
import { Button } from '../../../../../design-system/button/button';
import { Inline } from '../../../../../design-system/inline/inline';
import { Stack } from '../../../../../design-system/stack/stack';
import { exhaustiveCheck } from '../../../../../utils/exhaustive-check';

type AccountType = 'EXTERNAL' | 'SEAPOINT';

type AccountCardConfig =
  | {
      accountType: Extract<AccountType, 'EXTERNAL'>;
      onCtaClick: () => void;
    }
  | {
      accountType: Extract<AccountType, 'SEAPOINT'>;
      isLegalEntityInUk: boolean;
    };

type AccountTypeCardProps = {
  config: AccountCardConfig;
};

export const AccountTypeCard = ({ config }: AccountTypeCardProps) => {
  const { logos, title, description, featuresIntro, features, ctaText, isCtaEnabled, onCtaClick } = getCardContent({
    config,
  });

  return (
    <Stack
      space={5}
      justify="start"
      align="stretch"
      className="relative min-w-[300px] flex-1 overflow-clip rounded-xl border-[0.5px] border-border bg-white shadow-xs"
    >
      <Inline space={4} justify="center" className="header-gradient py-8">
        {logos.map((logo) => (
          <div key={logo.id} className="h-12 w-12 overflow-clip rounded-full bg-white">
            <img
              src={logo.src}
              alt=""
              loading="eager"
              fetchpriority="high"
              className="h-full w-full object-contain p-1"
            />
          </div>
        ))}
      </Inline>

      <Stack space={5} justify="between" className="flex-1 px-5 pb-5">
        <Stack space={4} justify="start" align="stretch" className="w-full">
          <Stack space={2}>
            <h3 className="text-xl font-semibold text-black">{title}</h3>

            <p className="text-sm font-medium text-muted-foreground">{description}</p>
          </Stack>

          <Stack space={2}>
            <p className="text-sm font-medium text-black">{featuresIntro}</p>

            <Stack space={1.5} as="ul" className="list-none" role="list" aria-label="Account features">
              {features.map((feature) => (
                <Inline
                  key={feature}
                  as="li"
                  wrap="nowrap"
                  align="start"
                  role="listitem"
                  aria-label={feature}
                  className="ml-2 text-sm font-normal text-muted-foreground before:mr-0.5 before:shrink-0 before:text-lg before:leading-none before:content-['•']"
                >
                  <span>{feature}</span>
                </Inline>
              ))}
            </Stack>
          </Stack>
        </Stack>

        <Button variant={isCtaEnabled ? 'default' : 'outline'} onClick={onCtaClick} disabled={!isCtaEnabled}>
          {ctaText}
        </Button>
      </Stack>
    </Stack>
  );
};

type GetCardContentArgs = {
  config: ComponentProps<typeof AccountTypeCard>['config'];
};

const getCardContent = ({ config }: GetCardContentArgs) => {
  const { accountType } = config;

  switch (accountType) {
    case 'EXTERNAL':
      return {
        logos: [
          { id: 'HSBC_LOGO', src: hsbcLogo },
          { id: 'BARCLAYS_LOGO', src: barclaysLogo },
        ],
        title: 'Connect an external account',
        description: 'Link your existing business accounts to Seapoint using Open Banking.',
        featuresIntro: 'Connecting an external account lets you:',
        features: [
          'View transactions from all your bank accounts in Seapoint',
          'Reconcile transactions in one place',
          'Stay multi-banked, with a unified financial view',
        ],
        ctaText: 'Connect Account',
        isCtaEnabled: true,
        onCtaClick: config.onCtaClick,
      };
    case 'SEAPOINT': {
      return {
        logos: [{ id: 'SEAPOINT_LOGO', src: seapointLogo }],
        title: 'Open a Seapoint account',
        description: config.isLegalEntityInUk
          ? 'A business account powered by Modulr, with funds safeguarded at the Bank of England.'
          : 'A business account powered by Modulr, with funds safeguarded with leading banks such as JP Morgan.',
        featuresIntro: 'A Seapoint business account will allow you to:',
        features: [
          'Transact with no limits on maximum balances or transaction value',
          'Create unlimited internal accounts',
          'Access 15+ global currencies',
        ],
        ctaText: 'Speak to our team to open an account',
        isCtaEnabled: false,
      };
    }
    default:
      return exhaustiveCheck(accountType);
  }
};
